import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { ArrowLeft, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Helmet } from 'react-helmet-async';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CaseStudiesList = () => {
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudies();
  }, []);

  const fetchCaseStudies = async () => {
    try {
      const response = await axios.get(`${API}/case-studies`);
      setStudies(response.data);
    } catch (error) {
      console.error('Error fetching case studies:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading case studies...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>E-commerce Success Stories & Case Studies | Obiquiste Solutions</title>
        <meta name="description" content="See how we helped brands grow on Amazon, Flipkart & quick commerce platforms. Real results, proven strategies. View our client success stories." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/case-studies" />
      </Helmet>
      <section className="py-16 md:py-24 bg-gradient-to-br from-slate-50 to-blue-50/50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 data-testid="case-studies-heading" className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
              Success <span className="text-gradient">Stories</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600">
              Real results from real brands. See how we've helped businesses scale profitably.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          {studies.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-slate-600">No case studies yet. Check back soon!</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {studies.map((study, idx) => (
                <motion.div
                  key={study.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-shadow"
                  data-testid={`case-study-item-${idx}`}
                >
                  {study.image_url && (
                    <img
                      src={study.image_url}
                      alt={study.title}
                      className="w-full h-56 object-cover"
                    />
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-semibold text-primary">
                        {study.marketplace}
                      </span>
                      <TrendingUp size={20} className="text-green-500" />
                    </div>
                    <h3 className="font-heading text-xl font-semibold mb-3">{study.title}</h3>
                    <p className="text-slate-600 mb-4 line-clamp-2">{study.challenge}</p>
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      {Object.entries(study.results).slice(0, 2).map(([key, value]) => (
                        <div key={key} className="text-center p-3 bg-slate-50 rounded-lg">
                          <div className="text-2xl font-bold text-primary">{value}</div>
                          <div className="text-xs text-slate-600 mt-1">{key}</div>
                        </div>
                      ))}
                    </div>
                    <Link to={`/case-studies/${study.slug}`}>
                      <Button variant="outline" className="w-full">
                        View Case Study
                      </Button>
                    </Link>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

const CaseStudyDetail = () => {
  const { slug } = useParams();
  const [study, setStudy] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCaseStudy();
  }, [slug]);

  const fetchCaseStudy = async () => {
    try {
      const response = await axios.get(`${API}/case-studies/${slug}`);
      setStudy(response.data);
    } catch (error) {
      console.error('Error fetching case study:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600">Loading case study...</p>
        </div>
      </div>
    );
  }

  if (!study) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Case study not found</h2>
          <Link to="/case-studies">
            <Button>Back to Case Studies</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24">
      <article className="py-16">
        <div className="container mx-auto px-4 md:px-6 max-w-6xl">
          <Link
            to="/case-studies"
            className="inline-flex items-center text-primary hover:underline mb-8"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Case Studies
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              {study.image_url && (
                <img
                  src={study.image_url}
                  alt={study.title}
                  className="w-full h-96 object-cover rounded-2xl mb-8"
                />
              )}

              <div className="mb-8">
                <div className="flex items-center space-x-4 mb-4">
                  <span className="px-4 py-2 bg-primary/10 text-primary rounded-full text-sm font-semibold">
                    {study.marketplace}
                  </span>
                  <span className="text-slate-600">{study.client_name}</span>
                </div>
                <h1 data-testid="case-study-title" className="font-heading text-4xl md:text-5xl font-bold mb-6">
                  {study.title}
                </h1>
              </div>

              <div className="space-y-8">
                <div>
                  <h2 className="font-heading text-2xl font-semibold mb-4">The Challenge</h2>
                  <p className="text-lg text-slate-600 leading-relaxed">{study.challenge}</p>
                </div>

                <div>
                  <h2 className="font-heading text-2xl font-semibold mb-4">Our Solution</h2>
                  <p className="text-lg text-slate-600 leading-relaxed">{study.solution}</p>
                </div>

                {study.testimonial && (
                  <div className="bg-slate-50 p-8 rounded-2xl border-l-4 border-primary">
                    <p className="text-lg italic text-slate-700">"{study.testimonial}"</p>
                    <p className="mt-4 font-semibold">- {study.client_name}</p>
                  </div>
                )}
              </div>
            </div>

            <div className="lg:col-span-1">
              <div className="bg-slate-50 p-8 rounded-2xl sticky top-24">
                <h3 className="font-heading text-2xl font-semibold mb-6">Results</h3>
                <div className="space-y-4">
                  {Object.entries(study.results).map(([key, value]) => (
                    <div key={key} className="bg-white p-6 rounded-xl shadow-sm">
                      <div className="text-3xl font-bold text-primary mb-2">{value}</div>
                      <div className="text-sm text-slate-600">{key}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </article>

      <section className="py-16 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-4xl font-semibold mb-6">
            Ready to Achieve Similar Results?
          </h2>
          <p className="text-xl text-slate-300 mb-8">
            Let's discuss how we can help grow your brand across marketplaces
          </p>
          <Link to="/contact">
            <Button className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full text-base font-semibold">
              Get Started Today
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export { CaseStudiesList, CaseStudyDetail };